import React from 'react';
import { CheckCircleIcon, DocumentIcon, PhotoIcon, PaperClipIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

interface EvidenceFile {
  id: string;
  filename: string;
  originalName?: string;
  mimeType: string;
  size?: number;
  uri?: string;
}

interface EvidenceCardProps {
  files: EvidenceFile[];
  className?: string;
}

const EvidenceCard: React.FC<EvidenceCardProps> = ({ files, className = '' }) => {
  const { t } = useTranslation();

  const getFileIcon = (mimeType: string) => {
    if (mimeType.startsWith('image/')) {
      return <PhotoIcon className="w-5 h-5" />;
    } else if (mimeType.includes('pdf') || mimeType.includes('document')) {
      return <DocumentIcon className="w-5 h-5" />;
    } else {
      return <PaperClipIcon className="w-5 h-5" />;
    }
  };

  const isVerified = (file: EvidenceFile) => {
    // Verify file format is valid and readable
    const validMimeTypes = [
      'application/pdf',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'text/plain'
    ];
    return validMimeTypes.some(type => file.mimeType.toLowerCase().includes(type.toLowerCase()));
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return '';
    const mb = bytes / (1024 * 1024);
    return mb < 1 ? `${(bytes / 1024).toFixed(1)} KB` : `${mb.toFixed(1)} MB`;
  };

  if (!files || files.length === 0) {
    return (
      <div className={`bg-yellow-50 border border-yellow-200 rounded-lg p-4 ${className}`}>
        <p className="text-yellow-800 text-sm">
          {t('filing.verification.evidence.noFiles', 'No evidence files uploaded yet')}
        </p>
      </div>
    );
  }

  return (
    <div className={`space-y-3 ${className}`}>
      {files.map((file, index) => {
        const verified = isVerified(file);
        return (
          <div
            key={file.id || index}
            className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200"
          >
            <div className="flex-shrink-0 text-gray-600">
              {getFileIcon(file.mimeType)}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {file.originalName || file.filename}
              </p>
              {file.size && (
                <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
              )}
            </div>
            {verified && (
              <div className="flex-shrink-0 flex items-center gap-1 text-green-600">
                <CheckCircleIcon className="w-5 h-5" />
                <span className="text-xs font-medium">
                  {t('filing.verification.evidence.verified', 'Verified')}
                </span>
              </div>
            )}
          </div>
        );
      })}
      <div className="pt-2 border-t border-gray-200">
        <p className="text-xs text-gray-600 flex items-center gap-1">
          <CheckCircleIcon className="w-4 h-4 text-green-600" />
          {t(
            'filing.verification.evidence.verificationNote',
            'All files verified for format and readability'
          )}
        </p>
      </div>
    </div>
  );
};

export default EvidenceCard;
